// Pit lane meshes: entry and exit roads and the pit lane surface (track
// asphalt), white boundary lines, the solid lines separating the pit entry and
// exit roads from the track, the fast-lane line, painted pit boxes, the 80 km/h
// pit entry/exit lines with "80" and signs, and the pit wall with its catch
// fence. Everything sits on the track elevation (each vertex takes the height
// of its own projection onto the centreline, like the track's own layers) a
// few centimetres above the track layers, with a stronger polygon offset, so
// nothing z-fights with the road, run-off or grass under it.
//
// buildPitLane also cuts the generated track barrier and catch fence where the
// pit roads cross them, so call it after buildTrackMeshes and buildFences.

import { Color3, DynamicTexture, Mesh, MeshBuilder, Scene, StandardMaterial, VertexData } from './babylon';
import { asphaltTexture, barrierTexture } from './textures';
import type { Track } from '../track/track';
import type { PitLane } from '../track/pitLane';

export { sepangPitLane } from '../track/pitLane';

/** Heights above the track elevation, metres. Track layers top out at 0.02 (lines). */
const LIFT = { surface: 0.03, lines: 0.04, paint: 0.045 } as const;
/** Polygon offsets: more negative draws on top. Track layers use -1 .. -6. */
const Z_OFFSET = { surface: -7, lines: -8, paint: -9 } as const;
const LINE_WIDTH = 0.2;
const SAMPLE = 2;
const WHITE: [number, number, number] = [0.93, 0.93, 0.9];
const YELLOW: [number, number, number] = [0.98, 0.8, 0.12];

class Builder {
  positions: number[] = [];
  normals: number[] = [];
  uvs: number[] = [];
  colors: number[] = [];
  indices: number[] = [];
  get count(): number {
    return this.positions.length / 3;
  }
  vertex(x: number, y: number, z: number, u: number, v: number, n: [number, number, number] = [0, 1, 0], c?: [number, number, number]): number {
    this.positions.push(x, y, z);
    this.normals.push(n[0], n[1], n[2]);
    this.uvs.push(u, v);
    if (c) this.colors.push(c[0], c[1], c[2], 1);
    return this.count - 1;
  }
  /** Two triangles a-b-c, a-c-d (both windings drawn: materials are two-sided). */
  quad(a: number, b: number, c: number, d: number): void {
    this.indices.push(a, b, c, a, c, d);
  }
  mesh(scene: Scene, name: string, mat: StandardMaterial): Mesh | null {
    if (!this.indices.length) return null;
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.uvs = this.uvs;
    vd.indices = this.indices;
    if (this.colors.length) vd.colors = this.colors;
    const mesh = new Mesh(name, scene);
    vd.applyToMesh(mesh, false);
    mesh.material = mat;
    mesh.useVertexColors = this.colors.length > 0;
    mesh.hasVertexAlpha = false;
    mesh.isPickable = false;
    mesh.freezeWorldMatrix();
    return mesh;
  }
}

function flatMaterial(scene: Scene, name: string, diffuse: Color3, zOffset: number, opts: { texture?: DynamicTexture; specular?: number; power?: number } = {}): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseColor = diffuse;
  if (opts.texture) mat.diffuseTexture = opts.texture;
  mat.specularColor = new Color3(opts.specular ?? 0, opts.specular ?? 0, opts.specular ?? 0);
  if (opts.power) mat.specularPower = opts.power;
  mat.zOffset = zOffset;
  mat.backFaceCulling = false;
  return mat;
}

/** Height of the ground at (x, z): the track elevation at its projection onto the centreline. */
function groundAt(track: Track, x: number, z: number, hint?: number): { y: number; index: number } {
  const p = track.project(x, z, hint);
  return { y: track.elevationAt(p.s), index: p.index };
}

/** Track segment hint for a lap distance (centreline points are evenly spaced). */
const trackHint = (track: Track, s: number): number => Math.floor((s / track.length) * track.count) % track.count;

/**
 * A painted band through `pts` (x, z), `width` wide, lying on the ground
 * `lift` above the track elevation. With `closed`, the band is a loop.
 */
function band(b: Builder, track: Track, pts: ReadonlyArray<{ x: number; z: number }>, width: number, lift: number, color?: [number, number, number], hint?: number): void {
  if (pts.length < 2) return;
  let h = hint;
  let along = 0;
  let prev: [number, number] | null = null;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    const a = pts[Math.max(0, i - 1)]!;
    const c = pts[Math.min(pts.length - 1, i + 1)]!;
    const dx = c.x - a.x;
    const dz = c.z - a.z;
    const len = Math.hypot(dx, dz) || 1;
    const nx = dz / len;
    const nz = -dx / len;
    if (i > 0) along += Math.hypot(p.x - pts[i - 1]!.x, p.z - pts[i - 1]!.z);
    const l = { x: p.x - (nx * width) / 2, z: p.z - (nz * width) / 2 };
    const r = { x: p.x + (nx * width) / 2, z: p.z + (nz * width) / 2 };
    const gl = groundAt(track, l.x, l.z, h);
    h = gl.index;
    const gr = groundAt(track, r.x, r.z, h);
    const pair: [number, number] = [b.vertex(l.x, gl.y + lift, l.z, 0, along, undefined, color), b.vertex(r.x, gr.y + lift, r.z, width, along, undefined, color)];
    if (prev) b.quad(prev[0], prev[1], pair[1], pair[0]);
    prev = pair;
  }
}

/** Distances d0..d1 sampled every `step` metres, ends included. */
function steps(d0: number, d1: number, step: number): number[] {
  const out: number[] = [];
  const n = Math.max(1, Math.ceil((d1 - d0) / step));
  for (let k = 0; k <= n; k++) out.push(d0 + ((d1 - d0) * k) / n);
  return out;
}

/** Text or symbol painted on a canvas, alpha-tested. */
function canvasTexture(scene: Scene, name: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): DynamicTexture {
  const tex = new DynamicTexture(name, { width: w, height: h }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, w, h);
  draw(ctx);
  tex.hasAlpha = true;
  tex.update(true);
  tex.anisotropicFilteringLevel = 8;
  return tex;
}

function alphaMaterial(scene: Scene, name: string, tex: DynamicTexture, zOffset = 0, emissive = 0.1): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseTexture = tex;
  mat.useAlphaFromDiffuseTexture = true;
  mat.transparencyMode = 1; // alpha test
  mat.alphaCutOff = 0.4;
  mat.backFaceCulling = false;
  mat.twoSidedLighting = true;
  mat.specularColor = Color3.Black();
  mat.emissiveColor = new Color3(emissive, emissive, emissive);
  mat.zOffset = zOffset;
  return mat;
}

/** Chain-link fence (as fences.ts), one tile per 3 m post bay. */
function fenceTexture(scene: Scene, height: number): DynamicTexture {
  const S = 256;
  const tex = canvasTexture(scene, 'tex-pit-fence', S, S, (ctx) => {
    ctx.strokeStyle = 'rgba(185,190,195,1)';
    ctx.lineWidth = 1.6;
    for (let k = -S; k < S * 2; k += 16) {
      ctx.beginPath();
      ctx.moveTo(k, 0);
      ctx.lineTo(k + S, S);
      ctx.moveTo(k + S, 0);
      ctx.lineTo(k, S);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(120,125,130,1)';
    ctx.fillRect(0, 0, 10, S);
    ctx.fillRect(0, 0, S, 6);
    ctx.fillRect(0, S - 6, S, 6);
  });
  tex.wrapU = 1;
  tex.wrapV = 1;
  tex.uScale = 1 / 3;
  tex.vScale = 1 / height;
  return tex;
}

/** Concrete with faint formwork joints every 2 m; UVs in metres. */
function concreteTexture(scene: Scene): DynamicTexture {
  const tex = new DynamicTexture('tex-pit-concrete', { width: 128, height: 64 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.fillStyle = '#c9c9c4';
  ctx.fillRect(0, 0, 128, 64);
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.fillRect(0, 0, 2, 64);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(0, 58, 128, 6);
  tex.update(true);
  tex.wrapU = 1;
  tex.wrapV = 1;
  tex.uScale = 1 / 2;
  tex.vScale = 1;
  return tex;
}

/** Pit lane speed sign: red ring with "80", or the white end-of-limit roundel with a black diagonal band. */
function signTexture(scene: Scene, kind: 'limit' | 'end'): DynamicTexture {
  return canvasTexture(scene, `tex-pit-sign-${kind}`, 128, 128, (ctx) => {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(64, 64, 60, 0, Math.PI * 2);
    ctx.fill();
    if (kind === 'limit') {
      ctx.strokeStyle = '#d4121a';
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.arc(64, 64, 52, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#111111';
      ctx.font = 'bold 54px Arial, Helvetica, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('80', 64, 68);
    } else {
      ctx.strokeStyle = '#222222';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(64, 64, 58, 0, Math.PI * 2);
      ctx.stroke();
      ctx.save();
      ctx.beginPath();
      ctx.arc(64, 64, 56, 0, Math.PI * 2);
      ctx.clip();
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = 6;
      for (const o of [-14, -5, 4, 13]) {
        ctx.beginPath();
        ctx.moveTo(10 + o, 118 + o);
        ctx.lineTo(118 + o, 10 + o);
        ctx.stroke();
      }
      ctx.restore();
    }
    // Grey back rim so it reads from behind, too.
    ctx.strokeStyle = 'rgba(90,90,90,1)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(64, 64, 61, 0, Math.PI * 2);
    ctx.stroke();
  });
}

/** Remove triangles of a static mesh whose centroid lies on the pit footprint. */
function cutMesh(mesh: Mesh | null, pit: PitLane, margin: number): void {
  if (!mesh) return;
  const pos = mesh.getVerticesData('position');
  const idx = mesh.getIndices();
  if (!pos || !idx) return;
  const keep: number[] = [];
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t]!;
    const b = idx[t + 1]!;
    const c = idx[t + 2]!;
    const x = (pos[a * 3]! + pos[b * 3]! + pos[c * 3]!) / 3;
    const z = (pos[a * 3 + 2]! + pos[b * 3 + 2]! + pos[c * 3 + 2]!) / 3;
    if (!pit.inFootprint(x, z, margin)) keep.push(a, b, c);
  }
  if (keep.length !== idx.length) mesh.setIndices(keep);
}

/**
 * Open the generated track barrier and its catch fence where the pit lane
 * crosses them (they are built along the whole lap by trackMesh/fences).
 * Visual only; the sim's barriers are separate.
 */
export function cutBarriersForPitLane(scene: Scene, pit: PitLane): void {
  cutMesh(scene.getMeshByName('track-barriers') as Mesh | null, pit, 0.6);
  cutMesh(scene.getMeshByName('fences') as Mesh | null, pit, 0.6);
}

export interface PitLaneMeshOptions {
  /** Freeze materials once the scene is ready (default true), as the track meshes do. */
  freezeMaterials?: boolean;
}

/** Build the pit lane meshes for a track. Call after buildTrackMeshes and buildFences. */
export function buildPitLane(scene: Scene, track: Track, pit: PitLane, options: PitLaneMeshOptions = {}): { meshes: Mesh[] } {
  const L = pit.length;
  const side = pit.entryOffsetSide;

  // ---- Surface: entry road, pit lane, exit road (track asphalt), clipped at the track's white line.
  const surface = new Builder();
  const runs: Array<Array<NonNullable<ReturnType<PitLane['crossSection']>>>> = [];
  let run: Array<NonNullable<ReturnType<PitLane['crossSection']>>> = [];
  for (const d of steps(0, L, SAMPLE)) {
    const cs = pit.crossSection(d);
    if (cs) run.push(cs);
    else if (run.length) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length) runs.push(run);
  for (const r of runs) {
    let prev: number[] | null = null;
    for (const cs of r) {
      const f = pit.poseAt(cs.d);
      // Lateral metres from the path (positive = right), for texture continuity.
      const lat = (p: { x: number; z: number }): number => (p.x - f.x) * f.tz - (p.z - f.z) * f.tx;
      const width = Math.hypot(cs.right.x - cs.left.x, cs.right.z - cs.left.z);
      const cols = Math.max(1, Math.ceil(width / 2.5));
      const row: number[] = [];
      let hint = trackHint(track, pit.trackOffsetAt(cs.d).s);
      for (let j = 0; j <= cols; j++) {
        const t = j / cols;
        const x = cs.left.x + (cs.right.x - cs.left.x) * t;
        const z = cs.left.z + (cs.right.z - cs.left.z) * t;
        const g = groundAt(track, x, z, hint);
        hint = g.index;
        row.push(surface.vertex(x, g.y + LIFT.surface, z, lat({ x, z }), cs.d));
      }
      if (prev && prev.length === row.length) {
        for (let j = 0; j < row.length - 1; j++) surface.quad(prev[j]!, prev[j + 1]!, row[j + 1]!, row[j]!);
      } else if (prev) {
        // Column count changed: stitch with a fan from the longer row.
        const [a, b] = prev.length > row.length ? [prev, row] : [row, prev];
        for (let j = 0; j < a.length - 1; j++) {
          const k = Math.min(b.length - 1, Math.round((j / (a.length - 1)) * (b.length - 1)));
          const k2 = Math.min(b.length - 1, Math.round(((j + 1) / (a.length - 1)) * (b.length - 1)));
          surface.indices.push(a[j]!, a[j + 1]!, b[k2]!);
          if (k !== k2) surface.indices.push(a[j]!, b[k2]!, b[k]!);
        }
      }
      prev = row;
    }
  }

  // ---- White lines.
  const lines = new Builder();
  const inset = (cs: NonNullable<ReturnType<PitLane['crossSection']>>, fromLeft: boolean, by: number): { x: number; z: number } => {
    const dx = cs.right.x - cs.left.x;
    const dz = cs.right.z - cs.left.z;
    const len = Math.hypot(dx, dz) || 1;
    const p = fromLeft ? cs.left : cs.right;
    const k = fromLeft ? by / len : -by / len;
    return { x: p.x + dx * k, z: p.z + dz * k };
  };
  const trackSideIsLeft = side > 0;
  for (const r of runs) {
    // Outer (garage-side) edge line all along; track-side edge line only away from the track.
    band(lines, track, r.filter((cs) => Math.hypot(cs.right.x - cs.left.x, cs.right.z - cs.left.z) > 1).map((cs) => inset(cs, !trackSideIsLeft, 0.25)), LINE_WIDTH, LIFT.lines);
    let seg: Array<{ x: number; z: number }> = [];
    for (const cs of r) {
      const onEdge = cs.leftOnTrackEdge;
      if (!onEdge && cs.d < pit.wall.from) seg.push(inset(cs, trackSideIsLeft, 0.25));
      else if (seg.length) {
        band(lines, track, seg, LINE_WIDTH, LIFT.lines);
        seg = [];
      }
    }
    band(lines, track, seg, LINE_WIDTH, LIFT.lines);
  }
  // Fast lane / inner (working) lane separation along the garages.
  // Boxes are centred 3 m off the garage fronts; the line runs just outside them.
  const boxWidth = pit.boxes[0]?.width ?? 3;
  band(lines, track, steps(pit.garages.from - 10, pit.garages.to + 10, SAMPLE).map((d) => pit.poseAt(d, side * (pit.garageFrontAt(d) - 3 - boxWidth / 2 - 0.35))), LINE_WIDTH, LIFT.lines);
  // Solid lines on the track separating the pit entry / exit road from the circuit:
  // along the road's track-side edge where it lies inside the track's white lines.
  let entryNose: { x: number; z: number } | undefined;
  const separation = (d0: number, d1: number): void => {
    let seg: Array<{ x: number; z: number }> = [];
    for (const d of steps(d0, d1, 1)) {
      const p = pit.poseAt(d, -side * 3.5);
      const pr = track.project(p.x, p.z, trackHint(track, pit.trackOffsetAt(d).s));
      const a = side * pr.offset;
      const edge = side > 0 ? pr.widthRight : pr.widthLeft;
      if (a > 0.6 && a < edge - 0.1) {
        // The bollard stands at the nose where the entry road's edge leaves the track.
        if (d0 === 0) entryNose = p;
        seg.push(p);
      } else if (seg.length) {
        band(lines, track, seg, LINE_WIDTH, LIFT.lines);
        seg = [];
      }
    }
    band(lines, track, seg, LINE_WIDTH, LIFT.lines);
  };
  separation(0, Math.min(L, 120));
  separation(Math.max(pit.wall.to, L - 160), L);
  // Pit entry and exit lines (speed limit start / end), straight across the lane.
  for (const d of [pit.speedLimitStart, pit.speedLimitEnd]) {
    const cs = pit.crossSection(d);
    if (cs) band(lines, track, [inset(cs, true, 0.05), inset(cs, false, 0.05)], 0.5, LIFT.paint);
  }

  // ---- Pit boxes (yellow for ours), with a stop bar where the front wheels go.
  const paint = new Builder();
  for (const box of pit.boxes) {
    const c = box.ours ? YELLOW : WHITE;
    const f = pit.poseAt(box.d);
    const at = (along: number, across: number): { x: number; z: number } => ({
      x: f.x + f.tx * along + f.tz * (box.offset + across),
      z: f.z + f.tz * along - f.tx * (box.offset + across),
    });
    const hl = box.length / 2;
    const hw = box.width / 2;
    const w = 0.12;
    for (const [p, q] of [
      [at(-hl, -hw), at(hl, -hw)],
      [at(-hl, hw), at(hl, hw)],
      [at(-hl - w / 2, -hw), at(-hl - w / 2, hw)],
      [at(hl + w / 2, -hw), at(hl + w / 2, hw)],
    ] as const) {
      band(paint, track, [p, q], w, LIFT.paint, c);
    }
    // Front wheel stop bar (front wheels ~1.6 m behind the nose) and centre line.
    band(paint, track, [at(hl - 1.6, -hw), at(hl - 1.6, hw)], 0.3, LIFT.paint, c);
    if (box.ours) band(paint, track, [at(-hl + 0.4, 0), at(hl - 0.4, 0)], 0.15, LIFT.paint, c);
  }

  const posts = new Builder();

  // ---- Road lettering: "80" after the pit entry line, "PIT" on the entry road.
  const paintDecal = (builder: Builder, d: number, lateral: number, len: number, wid: number): void => {
    const f = pit.poseAt(d, lateral);
    let hint = trackHint(track, pit.trackOffsetAt(d).s);
    const corner = (along: number, across: number, u: number, v: number): number => {
      const x = f.x + f.tx * along + f.tz * across;
      const z = f.z + f.tz * along - f.tx * across;
      const g = groundAt(track, x, z, hint);
      hint = g.index;
      return builder.vertex(x, g.y + LIFT.paint, z, u, v);
    };
    // u left -> right as the driver sees it, v near -> far (glyph tops point down the lane).
    builder.quad(corner(-len / 2, -wid / 2, 0, 0), corner(-len / 2, wid / 2, 1, 0), corner(len / 2, wid / 2, 1, 1), corner(len / 2, -wid / 2, 0, 1));
  };
  /** Tall, narrow road lettering, stretched along the lane as painted on tracks. */
  const roadText = (name: string, text: string, w: number, h: number, sx: number, sy: number): DynamicTexture =>
    canvasTexture(scene, name, w, h, (ctx) => {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 150px Arial, Helvetica, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.translate(w / 2, h / 2);
      ctx.scale(sx, sy);
      ctx.fillText(text, 0, 4);
    });
  const decal80 = new Builder();
  paintDecal(decal80, pit.speedLimitStart + 8, 0, 6, 3);
  const decalPit = new Builder();
  paintDecal(decalPit, 40, 0, 8, 4.5);

  // ---- A bollard at the nose of the pit entry, and a PIT IN board beside the entry road.
  const props: Mesh[] = [];
  if (entryNose) {
    const g = groundAt(track, entryNose.x, entryNose.z, trackHint(track, pit.entryS));
    const bollard = MeshBuilder.CreateCylinder('pit-bollard', { diameter: 0.35, height: 1.1, tessellation: 10 }, scene);
    bollard.position.set(entryNose.x, g.y + 0.55, entryNose.z);
    const bm = new StandardMaterial('pit-bollard-mat', scene);
    bm.diffuseColor = new Color3(1, 0.45, 0.05);
    bm.emissiveColor = new Color3(0.25, 0.1, 0);
    bm.specularColor = Color3.Black();
    bollard.material = bm;
    bollard.isPickable = false;
    bollard.freezeWorldMatrix();
    props.push(bollard);
  }
  const board = new Builder();
  {
    const f0 = pit.poseAt(0);
    const at = pit.poseAt(28, 8.5 * side);
    const g = groundAt(track, at.x, at.z, trackHint(track, pit.entryS));
    // Face the cars coming down the back straight: normal against the track direction at the split.
    const n: [number, number, number] = [-f0.tx, 0, -f0.tz];
    const hw = 1.8;
    const v = [
      board.vertex(at.x - f0.tz * hw, g.y + 1.0, at.z + f0.tx * hw, 0, 0, n),
      board.vertex(at.x + f0.tz * hw, g.y + 1.0, at.z - f0.tx * hw, 1, 0, n),
      board.vertex(at.x + f0.tz * hw, g.y + 2.4, at.z - f0.tx * hw, 1, 1, n),
      board.vertex(at.x - f0.tz * hw, g.y + 2.4, at.z + f0.tx * hw, 0, 1, n),
    ];
    board.quad(v[0]!, v[1]!, v[2]!, v[3]!);
    for (const k of [-1, 1]) {
      const px = at.x + f0.tz * hw * 0.8 * k;
      const pz = at.z - f0.tx * hw * 0.8 * k;
      const q = [
        posts.vertex(px - f0.tz * 0.06, g.y - 0.2, pz + f0.tx * 0.06, 0, 0, n),
        posts.vertex(px + f0.tz * 0.06, g.y - 0.2, pz - f0.tx * 0.06, 1, 0, n),
        posts.vertex(px + f0.tz * 0.06, g.y + 1.0, pz - f0.tx * 0.06, 1, 1, n),
        posts.vertex(px - f0.tz * 0.06, g.y + 1.0, pz + f0.tx * 0.06, 0, 1, n),
      ];
      posts.quad(q[0]!, q[1]!, q[2]!, q[3]!);
    }
  }
  const boardTex = canvasTexture(scene, 'tex-pit-board', 256, 96, (ctx) => {
    ctx.fillStyle = '#0d3b8c';
    ctx.fillRect(0, 0, 256, 96);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 5;
    ctx.strokeRect(5, 5, 246, 86);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 54px Arial, Helvetica, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('PIT', 26, 50);
    // Arrow bearing right.
    ctx.beginPath();
    ctx.moveTo(150, 38);
    ctx.lineTo(196, 38);
    ctx.lineTo(196, 22);
    ctx.lineTo(232, 48);
    ctx.lineTo(196, 74);
    ctx.lineTo(196, 58);
    ctx.lineTo(150, 58);
    ctx.closePath();
    ctx.fill();
  });

  // ---- Pit wall (concrete, boards on the track side) with a catch fence on top.
  const wallFace = new Builder();
  const wallBody = new Builder();
  const fence = new Builder();
  const fenceTop = 3.6;
  {
    const ds = steps(pit.wall.from, pit.wall.to, SAMPLE);
    let along = 0;
    let prev: { a: number[]; b: number[]; f: number[]; p: { x: number; z: number } } | null = null;
    type WallEnd = { x0: number; z0: number; y: number; ux: number; uz: number };
    let first: WallEnd | null = null;
    let last: WallEnd | null = null;
    for (const d of ds) {
      const cs = pit.crossSection(d);
      if (!cs) continue;
      const inner = side > 0 ? cs.left : cs.right;
      const outer = side > 0 ? cs.right : cs.left;
      const dx = outer.x - inner.x;
      const dz = outer.z - inner.z;
      const len = Math.hypot(dx, dz) || 1;
      const ux = dx / len;
      const uz = dz / len;
      const gap = 0.35;
      const A = { x: inner.x + ux * gap, z: inner.z + uz * gap };
      const B = { x: A.x + ux * pit.wall.thickness, z: A.z + uz * pit.wall.thickness };
      const g = groundAt(track, (A.x + B.x) / 2, (A.z + B.z) / 2, trackHint(track, pit.trackOffsetAt(d).s));
      const base = g.y - 0.3;
      const top = g.y + pit.wall.height;
      if (prev) along += Math.hypot(A.x - prev.p.x, A.z - prev.p.z);
      // Normals: track-side face points back at the track (-u), lane side +u.
      const nA: [number, number, number] = [-ux, 0, -uz];
      const nB: [number, number, number] = [ux, 0, uz];
      const a = [wallFace.vertex(A.x, base, A.z, -along * side, -0.3, nA), wallFace.vertex(A.x, top, A.z, -along * side, pit.wall.height, nA)];
      const b = [
        wallBody.vertex(A.x, top, A.z, along, 0, [0, 1, 0]),
        wallBody.vertex(B.x, top, B.z, along, pit.wall.thickness, [0, 1, 0]),
        wallBody.vertex(B.x, top, B.z, along, 0, nB),
        wallBody.vertex(B.x, base, B.z, along, pit.wall.height + 0.3, nB),
      ];
      const M = { x: (A.x + B.x) / 2, z: (A.z + B.z) / 2 };
      const fv = [fence.vertex(M.x, top, M.z, along, 0), fence.vertex(M.x, g.y + fenceTop, M.z, along, fenceTop - pit.wall.height)];
      if (prev) {
        wallFace.quad(prev.a[0]!, a[0]!, a[1]!, prev.a[1]!);
        wallBody.quad(prev.b[0]!, b[0]!, b[1]!, prev.b[1]!);
        wallBody.quad(prev.b[2]!, b[2]!, b[3]!, prev.b[3]!);
        fence.quad(prev.f[0]!, fv[0]!, fv[1]!, prev.f[1]!);
      }
      prev = { a, b, f: fv, p: A };
      const end = { x0: A.x, z0: A.z, y: g.y, ux, uz };
      if (!first) first = end;
      last = end;
    }
    // End caps.
    for (const e of [first, last]) {
      if (!e) continue;
      const bx = e.x0 + e.ux * pit.wall.thickness;
      const bz = e.z0 + e.uz * pit.wall.thickness;
      const n: [number, number, number] = [e.uz, 0, -e.ux];
      const q = [
        wallBody.vertex(e.x0, e.y - 0.3, e.z0, 0, 0, n),
        wallBody.vertex(bx, e.y - 0.3, bz, pit.wall.thickness, 0, n),
        wallBody.vertex(bx, e.y + pit.wall.height, bz, pit.wall.thickness, 1, n),
        wallBody.vertex(e.x0, e.y + pit.wall.height, e.z0, 0, 1, n),
      ];
      wallBody.quad(q[0]!, q[1]!, q[2]!, q[3]!);
    }
  }

  // ---- Speed signs on posts at the garage-side edge, facing the oncoming cars.
  const signs: Array<{ b: Builder; kind: 'limit' | 'end' }> = [];
  for (const [d, kind] of [
    [pit.speedLimitStart, 'limit'],
    [pit.speedLimitEnd, 'end'],
  ] as const) {
    const cs = pit.crossSection(d);
    if (!cs) continue;
    const f = pit.poseAt(d);
    const outer = side > 0 ? cs.right : cs.left;
    const ux = f.tz * side;
    const uz = -f.tx * side;
    const px = outer.x + ux * 0.6;
    const pz = outer.z + uz * 0.6;
    const g = groundAt(track, px, pz, trackHint(track, pit.trackOffsetAt(d).s));
    const r = 0.06;
    // Post: two crossed thin quads.
    for (const [ax, az] of [
      [f.tx, f.tz],
      [ux, uz],
    ] as const) {
      const v = [
        posts.vertex(px - ax * r, g.y - 0.2, pz - az * r, 0, 0, [az, 0, -ax]),
        posts.vertex(px + ax * r, g.y - 0.2, pz + az * r, 1, 0, [az, 0, -ax]),
        posts.vertex(px + ax * r, g.y + 2.3, pz + az * r, 1, 1, [az, 0, -ax]),
        posts.vertex(px - ax * r, g.y + 2.3, pz - az * r, 0, 1, [az, 0, -ax]),
      ];
      posts.quad(v[0]!, v[1]!, v[2]!, v[3]!);
    }
    // Sign disc (square, alpha-tested), 0.9 m, just in front of the post.
    const sb = new Builder();
    const s = 0.45;
    const cx = px - f.tx * 0.08;
    const cz = pz - f.tz * 0.08;
    const cy = g.y + 2.15;
    const n: [number, number, number] = [-f.tx, 0, -f.tz];
    // Seen from the approaching car: its right is the path's right (tz, -tx).
    const v = [
      sb.vertex(cx - f.tz * s, cy - s, cz + f.tx * s, 0, 0, n),
      sb.vertex(cx + f.tz * s, cy - s, cz - f.tx * s, 1, 0, n),
      sb.vertex(cx + f.tz * s, cy + s, cz - f.tx * s, 1, 1, n),
      sb.vertex(cx - f.tz * s, cy + s, cz + f.tx * s, 0, 1, n),
    ];
    sb.quad(v[0]!, v[1]!, v[2]!, v[3]!);
    signs.push({ b: sb, kind });
  }

  // ---- Meshes and materials.
  const meshes: Mesh[] = [];
  const add = (m: Mesh | null): void => {
    if (m) meshes.push(m);
  };
  add(surface.mesh(scene, 'pit-surface', flatMaterial(scene, 'pit-surface-mat', new Color3(0.68, 0.68, 0.7), Z_OFFSET.surface, { texture: asphaltTexture(scene), specular: 0.1, power: 40 })));
  add(lines.mesh(scene, 'pit-lines', flatMaterial(scene, 'pit-lines-mat', new Color3(0.92, 0.92, 0.9), Z_OFFSET.lines)));
  add(paint.mesh(scene, 'pit-paint', flatMaterial(scene, 'pit-paint-mat', new Color3(1, 1, 1), Z_OFFSET.paint)));
  add(decal80.mesh(scene, 'pit-80', alphaMaterial(scene, 'pit-80-mat', roadText('tex-pit-80', '80', 128, 256, 0.75, 1.6), Z_OFFSET.paint, 0.25)));
  add(decalPit.mesh(scene, 'pit-text', alphaMaterial(scene, 'pit-text-mat', roadText('tex-pit-text', 'PIT', 256, 256, 0.62, 1.5), Z_OFFSET.paint, 0.25)));
  add(board.mesh(scene, 'pit-board', alphaMaterial(scene, 'pit-board-mat', boardTex, 0, 0.35)));
  meshes.push(...props);
  const faceMat = new StandardMaterial('pit-wall-face-mat', scene);
  faceMat.diffuseTexture = barrierTexture(scene);
  faceMat.specularColor = new Color3(0.1, 0.1, 0.1);
  faceMat.backFaceCulling = false;
  add(wallFace.mesh(scene, 'pit-wall-face', faceMat));
  const bodyMat = new StandardMaterial('pit-wall-mat', scene);
  bodyMat.diffuseTexture = concreteTexture(scene);
  bodyMat.specularColor = Color3.Black();
  bodyMat.backFaceCulling = false;
  add(wallBody.mesh(scene, 'pit-wall', bodyMat));
  const fenceMat = alphaMaterial(scene, 'pit-fence-mat', fenceTexture(scene, fenceTop - pit.wall.height), 0, 0.15);
  fenceMat.alphaCutOff = 0.35;
  fenceMat.specularColor = new Color3(0.2, 0.2, 0.2);
  add(fence.mesh(scene, 'pit-fence', fenceMat));
  const postMat = new StandardMaterial('pit-post-mat', scene);
  postMat.diffuseColor = new Color3(0.55, 0.57, 0.6);
  postMat.specularColor = Color3.Black();
  postMat.backFaceCulling = false;
  add(posts.mesh(scene, 'pit-sign-posts', postMat));
  for (const s of signs) add(s.b.mesh(scene, `pit-sign-${s.kind}`, alphaMaterial(scene, `pit-sign-${s.kind}-mat`, signTexture(scene, s.kind), 0, 0.3)));

  for (const m of meshes) m.doNotSyncBoundingInfo = true;
  cutBarriersForPitLane(scene, pit);

  if (options.freezeMaterials !== false) {
    scene.executeWhenReady(() => {
      for (const m of meshes) if (!m.isDisposed()) m.material?.freeze();
    });
  }
  return { meshes };
}
